import { Fragment, useEffect, useRef, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

//Unform e componentes
import { Form } from "@unform/web";
import UInput from "../componentes/unform/UInput";

//Util
import { ICONES_BOTAO, ICONES_MENU, ICONES_SETA } from "../utils/icones";
import {
  fn_filtrarMenusPesquisa,
  fn_getPartesDestaquePesquisaMenu,
  fn_getRotaItemMenu,
  fn_isItemMenuAtivo,
} from "../utils/menu";

/*
 * Menu do sistema na lateral esquerda (alternativa ao menu no topo do header.jsx).
 * - Campo de pesquisa no alto: filtra as telas pelo nome (sem acento e sem diferenciar maiúscula/minúscula).
 *   Enter abre a primeira tela encontrada; Esc limpa a pesquisa; Ctrl+K, de qualquer lugar da tela, vai para a pesquisa.
 * - Menus em sanfona: só um aberto por vez. Ao abrir uma tela, fica aberto o menu dela, com o item destacado;
 *   o nome do menu da tela aberta tem um destaque leve, mesmo com o menu fechado.
 * - Recolhido: mostra só os ícones; clicar em um ícone abre o menu lateral já com esse menu aberto.
 * - O Dashboard e o botão de abrir/recolher ficam no header.
 * @menus: menus do usuário (mesma lista do menu no topo)
 * @recolhido / @onRecolher: menu recolhido (só ícones) e a função que recolhe/abre (o botão fica no header)
 */
function MenuLateral({ menus, recolhido, onRecolher }) {
  const navigate = useNavigate();
  const location = useLocation();

  //### USE REF ###
  const formRef = useRef(null);

  //### USE STATE ###
  const [textoPesquisa, setTextoPesquisa] = useState("");
  //Só um menu aberto por vez (id do menu aberto ou null)
  const [idMenuAberto, setIdMenuAberto] = useState(null);

  //### VARIÁVEIS AUXILIARES ###
  const isPesquisando = textoPesquisa.trim() != "";
  const menusApresentados = fn_filtrarMenusPesquisa(menus, textoPesquisa);

  //### USE EFFECT ###
  //Classe no body para o conteúdo da tela ficar ao lado do menu (a largura muda quando o menu é recolhido)
  useEffect(() => {
    document.body.classList.add("layout-menu-lateral");
    return () =>
      document.body.classList.remove(
        "layout-menu-lateral",
        "layout-menu-lateral-recolhido",
      );
  }, []);

  useEffect(() => {
    document.body.classList.toggle("layout-menu-lateral-recolhido", recolhido);
  }, [recolhido]);

  //Ao abrir uma tela: deixa aberto só o menu dela.
  //Depende só da rota: a lista de menus é montada de novo a cada atualização do header, e reabrir o menu da tela
  //nessas atualizações desfaria o menu que o usuário abriu
  useEffect(() => {
    const menuTela = getMenuTelaAberta();

    if (menuTela) {
      setIdMenuAberto(menuTela.id);
    }
  }, [location.pathname]);

  //Atalho Ctrl+K: coloca o foco na pesquisa (abre o menu, se estiver recolhido)
  useEffect(() => {
    function handleKeyDownAtalho(event) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() == "k") {
        event.preventDefault();
        focarPesquisa();
      }
    }

    document.addEventListener("keydown", handleKeyDownAtalho);
    return () => document.removeEventListener("keydown", handleKeyDownAtalho);
  }, [recolhido]);

  //### FUNÇÕES ###
  //Retorna o menu da tela aberta (o que tem o item da rota atual) ou undefined
  function getMenuTelaAberta() {
    return (menus || []).find((menu) => isMenuTelaAberta(menu));
  }

  //Indica se a tela aberta é de um item do menu
  function isMenuTelaAberta(menu) {
    return (menu.itens || []).some((item) =>
      fn_isItemMenuAtivo(item, location.pathname),
    );
  }

  //Coloca o foco no campo da pesquisa (e seleciona o texto), abrindo o menu se estiver recolhido
  function focarPesquisa() {
    if (recolhido) onRecolher(false);

    //aguarda o campo aparecer
    setTimeout(() => {
      const campo = formRef?.current?.getFieldRef("pesquisa_menu")?.current;
      campo?.focus();
      campo?.select();
    }, 50);
  }

  //Rola a lista do menu lateral até o menu (aguarda os itens aparecerem)
  function posicionarNoMenu(idMenu) {
    setTimeout(() => {
      document
        .getElementById(`menu-lateral-grupo-${idMenu}`)
        ?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    }, 50);
  }

  //Limpa a pesquisa
  function limparPesquisa() {
    formRef?.current?.setFieldValue("pesquisa_menu", "");
    setTextoPesquisa("");
  }

  //Abre a tela do item de menu
  function abrirItem(item) {
    const rota = fn_getRotaItemMenu(item);

    if (rota) {
      navigate(rota);
      limparPesquisa();
    }
  }

  //Apresenta o nome com o trecho pesquisado destacado
  function renderNome(nome) {
    return fn_getPartesDestaquePesquisaMenu(nome, textoPesquisa).map(
      (parte, index) =>
        parte.destaque ? (
          <mark key={index} className="menu-lateral-destaque">
            {parte.texto}
          </mark>
        ) : (
          <Fragment key={index}>{parte.texto}</Fragment>
        ),
    );
  }

  //Ícone do menu; sem ícone cadastrado, a primeira letra do nome
  function renderIconeMenu(menu) {
    return menu.icone && ICONES_MENU[menu.icone] ? (
      ICONES_MENU[menu.icone]
    ) : (
      <span className="menu-lateral-letra">{menu.nome?.charAt(0)}</span>
    );
  }

  //### EVENTOS ###
  //OnInput - Pesquisa (o UInput já tira o acento e passa para maiúsculo)
  function handleInputPesquisa(event) {
    setTextoPesquisa(event.target.value);
  }

  //OnKeyDown - Pesquisa: Enter abre a primeira tela encontrada; Esc limpa
  function handleKeyDownPesquisa(event) {
    if (event.key == "Enter") {
      event.preventDefault();
      const [primeiroItem] = menusApresentados.flatMap((menu) => menu.itens);
      if (isPesquisando && primeiroItem) abrirItem(primeiroItem);
    } else if (event.key == "Escape") {
      limparPesquisa();
    }
  }

  //OnClick - Menu: abre os itens dele e fecha os outros menus (clicar no menu aberto fecha).
  //Recolhido: abre o menu lateral já com o menu clicado aberto e posicionado nele
  function handleClickMenu(menu) {
    if (recolhido) {
      onRecolher(false);
      setIdMenuAberto(menu.id);
      posicionarNoMenu(menu.id);
      return;
    }

    const abrir = idMenuAberto != menu.id;
    setIdMenuAberto(abrir ? menu.id : null);
    if (abrir) posicionarNoMenu(menu.id);
  }

  return (
    <aside
      className={`menu-lateral${recolhido ? " menu-lateral-recolhido" : ""}`}
      aria-label="Menu"
    >
      {/* PESQUISA */}
      <div className="menu-lateral-pesquisa">
        {recolhido ? (
          <button
            type="button"
            className="menu-lateral-botao-icone"
            title="Pesquisar tela"
            onClick={focarPesquisa}
          >
            {ICONES_BOTAO.CONSULTAR}
          </button>
        ) : (
          <Form ref={formRef} onSubmit={() => {}}>
            <UInput
              name="pesquisa_menu"
              placeholder="Pesquisar tela"
              ariaLabel="Pesquisar tela"
              title="Digite parte do nome da tela. Enter abre a primeira encontrada e Esc limpa. Atalho para vir para a pesquisa de qualquer lugar da tela: Ctrl+K"
              icone={ICONES_BOTAO.CONSULTAR}
              className="form-control-sm"
              onInput={handleInputPesquisa}
              onKeyDown={handleKeyDownPesquisa}
            />
          </Form>
        )}
      </div>

      <div className="menu-lateral-lista" role="navigation">
        {/* MENUS */}
        {menusApresentados.map((menu) => {
          //Na pesquisa os menus encontrados ficam abertos
          const isAberto =
            !recolhido && (isPesquisando || idMenuAberto == menu.id);

          return (
            <div
              key={menu.id}
              id={`menu-lateral-grupo-${menu.id}`}
              className="menu-lateral-grupo"
            >
              <button
                type="button"
                className={`menu-lateral-menu${
                  isMenuTelaAberta(menu) ? " menu-lateral-menu-tela-aberta" : ""
                }`}
                title={recolhido ? menu.nome : undefined}
                aria-expanded={isAberto ? "true" : "false"}
                onClick={() => handleClickMenu(menu)}
              >
                <span className="menu-lateral-icone">
                  {renderIconeMenu(menu)}
                </span>
                <span className="menu-lateral-texto">
                  {renderNome(menu.nome)}
                </span>
                <span className="menu-lateral-seta">
                  {isAberto
                    ? ICONES_SETA.ABRIR_DETALHE
                    : ICONES_SETA.FECHAR_DETALHE}
                </span>
              </button>

              {/* ITENS DO MENU */}
              {isAberto && (
                <div className="menu-lateral-itens">
                  {menu.itens.map((item, index) => (
                    <Fragment key={item.id}>
                      {!isPesquisando &&
                        item?.divisor?.trim() == "S" &&
                        index > 0 && <div className="menu-lateral-divisor" />}

                      <button
                        type="button"
                        className={`menu-lateral-item${
                          fn_isItemMenuAtivo(item, location.pathname)
                            ? " menu-lateral-ativo"
                            : ""
                        }`}
                        onClick={() => abrirItem(item)}
                      >
                        {renderNome(item.nome)}
                      </button>
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {isPesquisando && menusApresentados.length == 0 && (
          <div className="menu-lateral-vazio">Nenhuma tela encontrada.</div>
        )}
      </div>
    </aside>
  );
}

export default MenuLateral;
